import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { mergeRegister } from "@lexical/utils";
import { ActionIcon, Box, Modal } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import type { LexicalEditor, NodeKey, NodeSelection, RangeSelection } from "lexical";
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  $setSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DRAGSTART_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Position } from "./InlineImageNode";

// Simple in-memory image caching for Suspense loading
const imageCache = new Set<string>();

function useSuspenseImage(src: string) {
  if (!imageCache.has(src)) {
    throw new Promise((resolve) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        imageCache.add(src);
        resolve(null);
      };
    });
  }
}

function LazyImage({
  altText,
  className,
  src,
  position,
}: {
  altText: string;
  className: string | null;
  src: string;
  position: Position;
}): JSX.Element {
  useSuspenseImage(src);
  return (
    <img
      className={className || undefined}
      src={src}
      alt={altText}
      data-position={position}
      style={{ maxWidth: "100%", maxHeight: 250, display: "block" }}
      draggable={false}
    />
  );
}

const InlineImageComponent: React.FC<{
  altText: string;
  caption: LexicalEditor; // legacy caption editor instance
  height: "inherit" | number;
  nodeKey: NodeKey;
  showCaption: boolean;
  src: string;
  width: "inherit" | number;
  position: Position;
}> = ({ src, altText, nodeKey, width, height, showCaption, caption, position }) => {
  const [showModal, setShowModal] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [selection, setSelection] = useState<RangeSelection | NodeSelection | null>(null);
  const activeEditorRef = useRef<LexicalEditor | null>(null);

  const isEditable = editor.isEditable();
  const isFocused = isSelected && isEditable;

  const handleDelete = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      node.remove();
    });
    setSelected(false);
  };

  const onDeleteKey = useCallback(
    (payload: KeyboardEvent) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        payload.preventDefault();
        handleDelete();
      }
      return false;
    },
    [isSelected],
  );

  const onEnter = useCallback(
    (event: KeyboardEvent) => {
      const latestSelection = $getSelection();
      const buttonElem = buttonRef.current;
      if (isSelected && $isNodeSelection(latestSelection) && latestSelection.getNodes().length === 1) {
        if (showCaption) {
          $setSelection(null);
          event.preventDefault();
          caption.focus();
          return true;
        } else if (buttonElem && buttonElem !== document.activeElement) {
          event.preventDefault();
          buttonElem.focus();
          return true;
        }
      }
      return false;
    },
    [isSelected, showCaption, caption],
  );

  const onEscape = useCallback(
    (event: KeyboardEvent) => {
      if (activeEditorRef.current === caption || buttonRef.current === event.target) {
        $setSelection(null);
        editor.update(() => {
          setSelected(true);
          editor.getRootElement()?.focus();
        });
        return true;
      }
      return false;
    },
    [caption, editor, setSelected],
  );

  useEffect(() => {
    let mounted = true;
    const unregister = mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        if (mounted) {
          editorState.read(() => {
            const sel = $getSelection();
            // Only store NodeSelection or RangeSelection; ignore null/other selection types
            if (sel == null || $isNodeSelection(sel) || sel.constructor.name === "RangeSelection") {
              setSelection(sel as RangeSelection | NodeSelection | null);
            } else {
              setSelection(null);
            }
          });
        }
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_, activeEditor) => {
          activeEditorRef.current = activeEditor;
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand<MouseEvent>(
        CLICK_COMMAND,
        (payload) => {
          if (payload.target === wrapperRef.current) {
            if (payload.shiftKey) {
              setSelected(!isSelected);
            } else {
              clearSelection();
              setSelected(true);
            }
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          if (event.target === wrapperRef.current) {
            event.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDeleteKey, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDeleteKey, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ENTER_COMMAND, onEnter, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ESCAPE_COMMAND, onEscape, COMMAND_PRIORITY_LOW),
    );
    return () => {
      mounted = false;
      unregister();
    };
  }, [editor, isSelected, clearSelection, onDeleteKey, onEnter, onEscape, setSelected]);

  const handleShowModal = () => {
    if (!isEditable) setShowModal(true);
  };

  return (
    <Suspense fallback={null}>
      <div
        ref={wrapperRef}
        onClick={handleShowModal}
        className={isEditable ? "editor-image-wrapper" : "editor-image-container"}
        style={{ position: "relative", display: "inline-block" }}
      >
        <LazyImage
          className={isFocused ? `focused ${$isNodeSelection(selection) ? "draggable" : ""}` : null}
          src={src}
          altText={altText}
          position={position}
        />
        {isEditable && (
          <ActionIcon
            variant="danger-filled"
            aria-label="Slett bilde"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            shadow="md"
            style={{ position: "absolute", bottom: 4, right: 4, boxShadow: "0 0 4px rgba(0,0,0,0.3)" }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        )}
      </div>
      <Modal
        opened={showModal}
        onClose={() => setShowModal(false)}
        size="60vw"
        yOffset={80}
        withCloseButton={false}
        overlayProps={{ blur: 2 }}
        keepMounted={false}
        zIndex={1200}
      >
        <Box className="gray-100" style={{ height: "auto", borderRadius: 6 }}>
          <img src={src} alt={altText} style={{ width: "100%", height: "auto" }} draggable={false} />
        </Box>
      </Modal>
    </Suspense>
  );
};

export default InlineImageComponent;
